import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { SectionHeader } from "../SectionHeader";
import { EASE } from "../motion/Reveal";
import { useContent } from "../../i18n/content";

const XS = [150, 450, 750, 1050];

const Node = ({ x, y, w, h, children, delay, onClick, testid }) => (
    <motion.g
        data-testid={testid}
        onClick={onClick}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onClick && onClick()}
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.05 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, delay, ease: EASE }}
        style={{ transformBox: "fill-box", transformOrigin: "center", cursor: "pointer" }}
        className="eco-node"
    >
        <rect x={x - w / 2} y={y - h / 2} width={w} height={h} fill="#32332c" stroke="#4a4c42" strokeWidth="1" />
        {children}
    </motion.g>
);

export const Ecosystem = () => {
    const { ui } = useContent();
    const navigate = useNavigate();
    const branches = ui.ecosystem.branches.map((b, i) => ({ ...b, x: XS[i] }));
    return (
    <section id="ecosystem" data-testid="ecosystem-section" className="relative py-24 lg:py-32 section-grid overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <SectionHeader
                chapter={ui.ecosystem.chapter}
                overline={ui.ecosystem.overline}
                title={ui.ecosystem.title}
                description={ui.ecosystem.description}
                align="center"
            />

            <div className="w-full overflow-x-auto no-scrollbar">
                <svg
                    viewBox="0 0 1200 520"
                    className="min-w-[760px] w-full max-w-5xl mx-auto"
                    role="img"
                    aria-label="Sadja Core Corp ecosystem diagram connecting Digon, Orbito and Sadja Group Indonesia"
                >
                    <defs>
                        <filter id="ecoGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="3" result="b" />
                            <feMerge>
                                <feMergeNode in="b" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {branches.map((b, i) => (
                        <g key={b.name}>
                            <motion.path
                                d={`M 600 110 C 600 190, ${b.x} 190, ${b.x} 250`}
                                fill="none"
                                stroke="#f6c422"
                                strokeWidth="1.5"
                                filter="url(#ecoGlow)"
                                initial={{ pathLength: 0, opacity: 0 }}
                                whileInView={{ pathLength: 1, opacity: 1 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 1.2, delay: 0.5 + i * 0.2, ease: EASE }}
                            />
                            <motion.path
                                d={`M ${b.x} 330 L ${b.x} 388`}
                                fill="none"
                                stroke="#4a4c42"
                                strokeWidth="1"
                                initial={{ pathLength: 0 }}
                                whileInView={{ pathLength: 1 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: 1.2 + i * 0.2, ease: EASE }}
                            />
                            <motion.circle
                                cx={b.x}
                                cy="250"
                                r="4"
                                fill="#f6c422"
                                initial={{ scale: 0 }}
                                whileInView={{ scale: 1 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ delay: 1.4 + i * 0.2, type: "spring", stiffness: 300 }}
                            />
                        </g>
                    ))}

                    <motion.g
                        initial={{ opacity: 0, scale: 0.92 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: EASE }}
                    >
                        <rect x="450" y="40" width="300" height="70" fill="#292a24" stroke="#f6c422" strokeWidth="1.5" />
                        <text x="600" y="70" textAnchor="middle" fill="#ffffff" fontFamily="Manrope, sans-serif" fontWeight="800" fontSize="17" letterSpacing="3">
                            SADJA CORE CORP
                        </text>
                        <text x="600" y="92" textAnchor="middle" fill="#f6c422" fontFamily="Manrope, sans-serif" fontSize="10" letterSpacing="4">
                            {ui.ecosystem.centerSub}
                        </text>
                    </motion.g>

                    {branches.map((b, i) => (
                        <g key={`${b.name}-nodes`}>
                            <Node x={b.x} y={290} w={230} h={80} delay={1 + i * 0.2} onClick={() => navigate(b.route)} testid={`ecosystem-node-${b.route.split("/").pop()}`}>
                                <text x={b.x} y={282} textAnchor="middle" fill="#ffffff" fontFamily="Manrope, sans-serif" fontWeight="800" fontSize="14" letterSpacing="2">
                                    {b.name}
                                </text>
                                <text x={b.x} y={306} textAnchor="middle" fill="#a2a59d" fontFamily="'Plus Jakarta Sans', sans-serif" fontSize="10.5">
                                    {b.sub}
                                </text>
                            </Node>
                            <motion.g
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ delay: 1.5 + i * 0.2, duration: 0.6 }}
                            >
                                <text x={b.x} y={420} textAnchor="middle" fill="#f6c422" fontFamily="Manrope, sans-serif" fontWeight="700" fontSize="13" letterSpacing="5">
                                    {b.tag}
                                </text>
                            </motion.g>
                        </g>
                    ))}
                </svg>
            </div>

            <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 1.8, duration: 0.8 }}
                className="text-center text-[#a2a59d] text-sm mt-10 max-w-xl mx-auto leading-relaxed"
            >
                {ui.ecosystem.caption}
            </motion.p>
        </div>
    </section>
    );
};
